import { useCallback, ChangeEventHandler } from 'react'
import Styles from './Steps.module.scss'
import cx from 'classnames'
import { Select } from 'components/Select/Select'
import { Item } from '@react-stately/collections'
import { DueDateDays, DEFAULT_DURATION } from './utils'
import { TextInputField } from 'components/Form/TextInputField'
import { PropertyValuePill } from 'components/PropertyValuePill/PropertyValuePill'
import { type PropertyValue } from 'api/clients/PropertyClient'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export type Props = {
  duration: string
  onDurationChange: (newDuration: string) => void
  onCustomDurationChange: (newCustomDuration: string) => void
  customDuration: string
  fromStatus: Pick<PropertyValue, 'value' | 'meta'>
  toStatus: Pick<PropertyValue, 'value' | 'meta'>
}

export const Steps = ({
  duration = DEFAULT_DURATION,
  onDurationChange,
  customDuration,
  onCustomDurationChange,
  fromStatus,
  toStatus,
}: Props) => {
  const { isMobile } = useResponsiveDesign()
  const displayText = useCallback(
    (selectedItem: any) => {
      const days = Object.values(DueDateDays).includes(selectedItem?.key) ? selectedItem.rendered : `${duration} days`

      return (
        <span className={Styles.startSelect}>
          Due date <span className={Styles.selectedDueDateLabel}>{days}</span>
        </span>
      )
    },
    [duration],
  )

  const handleCustomInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value.length > 3 || Number(e.target.value) < 0 || e.target.value === '0') return

    onCustomDurationChange(e.target.value)
  }

  const customInputCharLengthClassName = {
    2: Styles.twoChars,
    3: Styles.threeChars,
  }[customDuration.toString().length]

  return (
    <div className={Styles.stepsContainer}>
      <div className={Styles.step}>
        <div className={Styles.graph}>
          <div className={Styles.triangle}></div>
          <div className={Styles.line}></div>
        </div>
        <div className={Styles.stepInfo}>
          <div className={Styles.title}>Start</div>
          {!isMobile && <div className={Styles.description}>When the status is changed to</div>}
          <PropertyValuePill propertyValue={fromStatus} className={Styles.statusPill} />
        </div>
      </div>
      <div className={Styles.step}>
        <div className={Styles.graph}>
          <div className={Styles.circle}></div>
          <div className={Styles.line}></div>
        </div>
        <div className={Styles.stepInfo}>
          <div className={Styles.title}>Ends</div>
          {!isMobile && (
            <div className={Styles.description}>When all users approve, a user ends it manually, or on</div>
          )}
          <div className={Styles.durationInputs}>
            <Select
              selectedKey={duration}
              onSelectionChange={(value) => onDurationChange(value as string)}
              aria-label="due date on"
              variant="tertiary"
              size="lg"
              renderSelectValue={displayText}
            >
              <Item key={DueDateDays.ONE}>1 day</Item>
              <Item key={DueDateDays.THREE}>3 days</Item>
              <Item key={DueDateDays.FIVE}>5 days</Item>
              <Item key={DueDateDays.SEVEN}>7 days</Item>
              <Item key={DueDateDays.FOURTEEN}>14 days</Item>
              <Item key={DueDateDays.INDEFINITELY}>Indefinitely</Item>
              <Item key={DueDateDays.CUSTOM}>Custom</Item>
            </Select>

            {duration === DueDateDays.CUSTOM && (
              <TextInputField
                id="custom-duration"
                size="lg"
                onChange={handleCustomInputChange}
                placeholder={DEFAULT_DURATION}
                wrapperClassName={Styles.customInputWrapper}
                value={customDuration}
                className={cx(Styles.customDueDateInput, customInputCharLengthClassName, {
                  [Styles.oneDay]: customDuration === DueDateDays.ONE,
                })}
                aria-label="custom duration"
                type="number"
                onKeyDown={(e) => ['e', 'E', '+', '-', ',', '.'].includes(e.key) && e.preventDefault()}
              />
            )}
          </div>
        </div>
      </div>
      <div className={Styles.step}>
        <div className={Styles.graph}>
          <div className={Styles.circle}></div>
          <div className={cx(Styles.line, Styles.gradientLine)}></div>
        </div>
        <div className={Styles.stepInfo}>
          <div className={Styles.title}>Next</div>
          {!isMobile && <div className={Styles.description}>When the request ends, the doc automatically moves to</div>}
          <PropertyValuePill propertyValue={toStatus} className={Styles.statusPill} />
        </div>
      </div>
    </div>
  )
}
